import React, { useState } from 'react';
import { X, Building2, Video } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card } from './ui/Card';
import { SwipefileReferencePicker } from './SwipefileReferencePicker';
interface SwipefileReference {
  id: string;
  title: string;
  thumbnail: string;
  topic: string;
}
interface AddBrandProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (profile: {
    brandName: string;
    industry: string;
    targetAudience: string;
    brandVoice: string;
    keyMessages: string[];
    swipefileReferences: SwipefileReference[];
  }) => void;
}
// Mock swipefile data
const MOCK_SWIPEFILE: SwipefileReference[] = [{
  id: '1',
  title: 'How Compound Interest Actually Works',
  thumbnail: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&auto=format&fit=crop&q=60',
  topic: 'Financial Education'
}, {
  id: '2',
  title: '5 Banking Mistakes Gen Z Makes',
  thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60',
  topic: 'Gen Z Banking'
}, {
  id: '3',
  title: 'Understanding Your Credit Score',
  thumbnail: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&auto=format&fit=crop&q=60',
  topic: 'Credit & Loans'
}, {
  id: '4',
  title: 'Savings Hacks Nobody Tells You',
  thumbnail: 'https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?w=800&auto=format&fit=crop&q=60',
  topic: 'Savings Tips'
}, {
  id: '5',
  title: "What Banks Don't Want You to Know",
  thumbnail: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60',
  topic: 'Banking Secrets'
}, {
  id: '6',
  title: 'First Time Home Buyer Guide',
  thumbnail: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=60',
  topic: 'Mortgages'
}];
export function AddBrandProfileModal({
  isOpen,
  onClose,
  onSubmit
}: AddBrandProfileModalProps) {
  const [brandName, setBrandName] = useState('');
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [keyMessagesInput, setKeyMessagesInput] = useState('');
  const [selectedReferences, setSelectedReferences] = useState<SwipefileReference[]>([]);
  if (!isOpen) return null;
  const handleSubmit = () => {
    const keyMessages = keyMessagesInput.split(',').map(msg => msg.trim()).filter(msg => msg);
    if (!brandName.trim() || !industry.trim() || !targetAudience.trim() || !brandVoice.trim() || keyMessages.length === 0) return;
    onSubmit({
      brandName,
      industry,
      targetAudience,
      brandVoice,
      keyMessages,
      swipefileReferences: selectedReferences
    });
    // Reset form
    setBrandName('');
    setIndustry('');
    setTargetAudience('');
    setBrandVoice('');
    setKeyMessagesInput('');
    setSelectedReferences([]);
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-16">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white" data-id="element-17">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" data-id="element-18">
          <div className="flex items-center gap-3" data-id="element-19">
            <div className="h-10 w-10 rounded-lg bg-[#139191]/10 flex items-center justify-center" data-id="element-20">
              <Building2 className="h-5 w-5 text-[#139191]" data-id="element-21" />
            </div>
            <div data-id="element-22">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-23">
                New Brand Profile
              </h2>
              <p className="text-sm text-gray-500" data-id="element-24">
                Define your brand identity and voice
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-id="element-25">
            <X className="h-5 w-5" data-id="element-26" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-27">
          {/* Brand Name */}
          <div data-id="element-28">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-29">
              Brand Name *
            </label>
            <Input placeholder="e.g., einfachbank Professional" value={brandName} onChange={e => setBrandName(e.target.value)} className="text-base" data-id="element-30" />
          </div>

          {/* Industry */}
          <div data-id="element-31">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-32">
              Industry *
            </label>
            <Input placeholder="e.g., Banking & Finance" value={industry} onChange={e => setIndustry(e.target.value)} className="text-base" data-id="element-33" />
          </div>

          {/* Target Audience */}
          <div data-id="element-34">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-35">
              Target Audience *
            </label>
            <textarea placeholder="e.g., Financially savvy adults 30-55, professionals seeking transparent banking" value={targetAudience} onChange={e => setTargetAudience(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#139191] focus:border-transparent resize-none text-sm" data-id="element-36" />
          </div>

          {/* Brand Voice */}
          <div data-id="element-37">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-38">
              Brand Voice & Tone *
            </label>
            <textarea placeholder="e.g., Professional, trustworthy, transparent, educational" value={brandVoice} onChange={e => setBrandVoice(e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#139191] focus:border-transparent resize-none text-sm" data-id="element-39" />
          </div>

          {/* Key Messages */}
          <div data-id="element-40">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-41">
              Key Messages *
            </label>
            <Input placeholder="e.g., Transparency, No Hidden Fees, Financial Empowerment (comma-separated)" value={keyMessagesInput} onChange={e => setKeyMessagesInput(e.target.value)} className="text-base" data-id="element-42" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-43">
              Separate multiple messages with commas
            </p>
          </div>

          {/* Swipefile References */}
          <div data-id="element-44">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-45">
              <Video className="h-4 w-4 text-[#139191]" data-id="element-46" />
              Brand Reference Videos (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-4" data-id="element-47">
              Select videos that represent the style, tone, and approach you
              want for this brand.
            </p>
            <SwipefileReferencePicker availableReferences={MOCK_SWIPEFILE} selectedReferences={selectedReferences} onSelectionChange={setSelectedReferences} data-id="element-48" />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50" data-id="element-49">
          <p className="text-sm text-gray-500" data-id="element-50">
            {selectedReferences.length > 0 && `${selectedReferences.length} reference${selectedReferences.length > 1 ? 's' : ''} selected`}
          </p>
          <div className="flex gap-3" data-id="element-51">
            <Button variant="outline" onClick={onClose} data-id="element-52">
              Cancel
            </Button>
            <Button className="bg-[#139191] hover:bg-[#139191]/90 text-white" onClick={handleSubmit} disabled={!brandName.trim() || !industry.trim() || !targetAudience.trim() || !brandVoice.trim() || !keyMessagesInput.trim()} data-id="element-53">
              Create Brand Profile
            </Button>
          </div>
        </div>
      </Card>
    </div>;
}